import * as React from 'react';
import { AlertMessage, AlertType, Form, SpinnerWrapper } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { useParams } from 'react-router-dom';
import { setMessage } from '../../../helpers/MessageHelper';
import { ReferralApi } from "../../../api/ReferralApi";
import { IReferral } from '../../../models/IReferral';
import { CreateReferralFormInputs } from './CreateReferralFormInputs';
import { IPatient } from '../../../models/IPatient';
import { ReferralRejectModal } from './ReferralModals/ReferralResponseModals/ReferralRejectModal';
import { ReferralComplementModal } from './ReferralModals/ReferralResponseModals/ReferralComplementModal';
import { ReferralApproveModal } from './ReferralModals/ReferralResponseModals/ReferralApproveModal';
import { ReferralCopyModal } from './ReferralModals/ReferralCopyModal';
import { ReferralForwardModal } from './ReferralModals/ReferralForwardModal';
import { ReferralFooterButtons } from './ReferralButtons/ReferralFooterButtons';
import { FOLLOWUP_REFERRAL, FORWARD_REFERRAL } from '../../../utils/ReferralTypeConstants';
import { CreateReferenceReferralButton } from './ReferralButtons/CreateReferenceReferralButton';
import { ReferralDropdownButtons } from './ReferralButtons/ReferralDropdownButtons';
import './Referral.scss';
import { ReferralComments } from './ReferralComments';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { VÅRDANSVARIG } from '../../../utils/RoleConstants';
import { EventLog } from "../../../components/EventLog/EventLog";

interface IReferralView {
    patient: IPatient;
}

export const Referral: React.FunctionComponent<IReferralView> = ({ patient }) => {
    const auth = useAuth();
    const api = new ReferralApi(auth);
    const [referral, setReferral] = React.useState<IReferral>();
    const { id } = useParams<{id: string}>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [isFetchingReferral, setIsFetchingReferral] = React.useState<boolean>(false);
    const [canFetchReferral, setCanFetchReferral] = React.useState<boolean>(true);
    const [gotReportedTreatments, setGotReportedTreatments] = React.useState<boolean>(false);
    const userInfo = React.useContext(UserContext);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles ? userInfo.roles : []);

    React.useEffect(() => {
        if (id) {
            getReferral(parseInt(id), false);
        } else if (referral === undefined) {
            setReferral({
                isEditable: true,
                canSendResponse: false,
                canAssessTreatments: false,
                canReportTreatments: false,
                canWithdrawReferral: false,
                canCopyReferral: false,
                canForwardReferral: false,
                canResetReferral: false,
                referralId: undefined,
                referralReason: "",
                referralText: "",
                referralStatus: "",
                referrerName: "",
                unreadResponse: false,
                isShareable: false,
                patientEmail: "",
                patientPhoneNumber: "",
                canWriteAnswer: false,
                isDentist: true,
                isAllowedToReadReportedTreatments: false,
                canEditRecipientCareProvider: true
            });
        }
    }, [id])

    const getReferral = (id: number, refresh: boolean) => {
        setIsFetchingReferral(true);
        api.getReferral(id, refresh, (newReferral) => {
            setReferral(newReferral);
        }, (error) => {
            setCanFetchReferral(false);
            changeErrorMessage(error);
        }, () => {
            setIsFetchingReferral(false);
        });
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const changeErrorMessage = (message: string) => {
        const newMessage = setMessage(message, errorMessage ?? "");
        setErrorMessage(newMessage);
    }

    const formSuccess = (message: string, referralId?: number) => {
        changeSuccessMessage(message);
        if (referralId) {
            getReferral(referralId, true);
        }
        else if (referral?.referralId) {
            getReferral(referral?.referralId, true);
        }
    }

    const copyModalSuccess = (copiedData: IReferral, successMessage: string) => {
        copiedData.isCopy = true;
        setReferral(copiedData);
        changeSuccessMessage(successMessage);
    }

    const forwardModalSuccess = (newData: IReferral, successMessage: string) => {
        setReferral(newData);
        if (successMessage !== "") {
            changeSuccessMessage(successMessage);
        }
    }

    const getReferralHeader = () => {
        if (referral?.referralTypeId === FOLLOWUP_REFERRAL) {
            return "Uppföljningsremiss";
        }
        if (referral?.referralTypeId === FORWARD_REFERRAL) {
            return "Specialistremiss";
        }
        return "Specialistremiss";
    }

    return (
        <SpinnerWrapper isLoading={isFetchingReferral}>
            <Form>
                <section className="card">
                    <div className="card-header">
                        <div className="row referral-header-row">
                            <div className="col">
                                <h1>{getReferralHeader()} {referral?.referralNumber ? ` - ${referral.referralNumber}` : ""}</h1>
                            </div>
                            {
                                referral?.unreadResponse ?
                                    <div className="col">
                                        <h2 className="text-center">*** Oläst ***</h2>
                                    </div>
                                    : null
                            }
                            <div className="col text-right">
                                <CreateReferenceReferralButton canCreateForwardReferral={referral?.canForwardReferral} />
                                <ReferralDropdownButtons canShare={referral?.isShareable!} changeSuccessMessage={changeSuccessMessage} changeErrorMessage={changeErrorMessage} referralId={referral?.referralId} getReferral={(id) => getReferral(id, true)} />
                            </div>
                        </div>
                    </div>
                    <div className="card-body">
                        <AlertMessage alertType={AlertType.danger} duration={3000}>{errorMessage}</AlertMessage>
                        <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                        {
                            canFetchReferral && referral ?
                                <CreateReferralFormInputs referral={referral} isFetchingReferral={isFetchingReferral} patient={patient} getReferral={(id) => getReferral(id, true)} gotReportedTreatments={setGotReportedTreatments} />
                                : null
                        }
                        <ReferralComments referralComments={referral?.referralComments} />
                        {
                            referral?.events ?
                                <>
                                    <hr />
                                    <EventLog id="referralEventLog" events={referral.events} />
                                </>
                                : null
                        }
                    </div>
                    <div className="card-footer">
                        <ReferralFooterButtons referral={referral} getReferral={(id) => getReferral(id, true)} changeSuccessMessage={changeSuccessMessage} changeErrorMessage={changeErrorMessage} gotTreatmentsReported={gotReportedTreatments} isParaply={hasRoleParaply} />
                    </div>
                </section>
            </Form>
            {
                referral?.referralId ?
                    <>
                        <ReferralRejectModal onSuccess={formSuccess} referralId={referral.referralId} />
                        <ReferralComplementModal onSuccess={formSuccess} referralId={referral.referralId} />
                        <ReferralApproveModal onSuccess={formSuccess} referralId={referral.referralId} />
                        <ReferralCopyModal onSuccess={copyModalSuccess} referralId={referral.referralId} patientGuidId={patient.patientGuidId} />
                        <ReferralForwardModal onSuccess={forwardModalSuccess} referralId={referral.referralId} patientGuidId={patient.patientGuidId} />
                    </>
                    : null
            }
        </SpinnerWrapper>
    )
}